import { Select, TextInput } from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { CourseEnrollment, CreateValidationFields } from '../../types';
import { useValidations } from '../../../../hooks/useValidations';

const prefix = 'courseValidationInternalStudent.curriculum.modal';

interface InternalValidationFormProps {
  courseCode?: string;
  courseName?: string;
  readonly?: boolean;
  courseEnrollments?: CourseEnrollment[];
}

const InternalValidationForm = ({
  readonly = false,
  courseCode,
  courseName,
  courseEnrollments = [],
}: InternalValidationFormProps) => {
  const { t } = useTranslation();
  const { msgValidations } = useValidations();

  const { control, watch } = useFormContext<CreateValidationFields>();

  const [selectedCourseEnrollment] = watch(['internal.courseEnrollmentId']);
  const texts = useMemo(
    () => ({
      title: t(`${prefix}.title`),
      body: t(`${prefix}.body`),
      label: {
        courseToValidate: t(`${prefix}.label.courseToValidate`),
        courseCodeLabel: t(
          `courseValidationInternalStudent.modalValidationType.courseCodeLabel`,
        ),
        course: t(`${prefix}.label.course`),
        code: t(`${prefix}.label.code`),
        credits: t(`${prefix}.label.credits`),
        grade: t(`${prefix}.label.grade`),
      },
      cancel: t(`common.actions.cancel`),
      accept: t(`${prefix}.accept`),
      error: t(`${prefix}.error`),
      required: msgValidations.required,
    }),
    [t, msgValidations],
  );

  if (readonly) {
    return (
      <Row className="mb-4">
        <Col xs={12}>
          <TextInput
            name="code"
            label={texts.label.courseCodeLabel}
            value={courseCode || courseName}
            disabled
          />
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mb-4">
      <Col xs={12} md={selectedCourseEnrollment ? 8 : undefined}>
        <Select
          name="internal.courseEnrollmentId"
          label={texts.label.courseToValidate}
          options={courseEnrollments.map((c) => ({
            label: c.course.name || '',
            value: c.id,
            courseEnrollment: c,
          }))}
          control={control}
          disabled={readonly}
          rules={{ required: texts.required }}
        />
      </Col>

      {!!selectedCourseEnrollment && (
        <>
          <Col xs={12} md={4}>
            <TextInput
              name="code"
              label={texts.label.code}
              value={
                selectedCourseEnrollment.courseEnrollment.course.shortening
              }
              disabled
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="credits"
              label={texts.label.credits}
              value={selectedCourseEnrollment.courseEnrollment.course.credits}
              disabled
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="grade"
              label={texts.label.grade}
              value={selectedCourseEnrollment.courseEnrollment.grade}
              disabled
            />
          </Col>
        </>
      )}
    </Row>
  );
};

export default InternalValidationForm;
